<template>
    <div id="address-settings">
        <div class="form-field">
            <label for="field-label" class="form-label">{{ $t("form.fieldLabel") }}</label>
            <input
                type="text"
                id="field-label"
                class="form-input"
                :placeholder="$t('message.address')"
                v-model="fieldAddress"
                @input="setFieldLabelAddress(fieldAddress)"
            >
        </div>

        <div class="form-field">
            <label class="form-label">{{ $t("form.labelAlignment") }}</label>
            <div class="button-group">
                <button class="button" :class="{ active: align === 'left' }" @click="setLabelAlignAddress('left')">{{ $t("form.left") }}</button>
                <button class="button" :class="{ active: align === 'right' }"@click="setLabelAlignAddress('right')">{{ $t("form.right") }}</button>
                <button class="button" :class="{ active: align === 'top' }" @click="setLabelAlignAddress('top')">{{ $t("form.top") }}</button>
            </div>
            <small class="form-description">{{ $t("form.chooseLabelAlignment") }}</small>
        </div>

        <div class="form-field">
            <label class="form-label">{{ $t("form.required") }}</label>
            <div class="toggle">
                <span class="toggle-option" :class="{ active: isRequired }"
                      @click="toggleRequired">{{ requiredText }}</span>
            </div>
            <small class="form-description">{{ $t("form.fieldEmptyError") }}</small>
        </div>

        <div class="form-field">
            <label class="form-label">{{ $t("form.subLabels") }}</label>
            <div class="sub-labels">
                <div class="sub-label-item">
                    <label for="address-line-1" class="sub-label">{{ $t("form.addressLine1") }}</label>
                    <input
                        type="text"
                        id="address-line-1"
                        class="sub-label-input"
                        :placeholder="$t('form.addressLine1')"
                        v-model="addressLine1"
                        @input="setFieldAddressLine1(addressLine1)"
                    >
                </div>
                <div class="sub-label-item">
                    <label for="address-line-2" class="sub-label">{{ $t("form.addressLine2") }}</label>
                    <input
                        type="text"
                        id="address-line-2"
                        class="sub-label-input"
                        :placeholder="$t('form.addressLine2')"
                        v-model="addressLine2"
                        @input="setFieldAddressLine2(addressLine2)"
                    >
                </div>
                <div class="sub-label-item">
                    <label for="district" class="sub-label">{{ $t("akinon.city") }}</label>
                    <input
                        type="text"
                        id="district"
                        class="sub-label-input"
                        :placeholder="$t('akinon.city')"
                        v-model="district"
                        @input="setFieldDistrict(district)"
                    >
                </div>
                <div class="sub-label-item">
                    <label for="postal-code" class="sub-label">{{ $t("form.postalCode") }}</label>
                    <input
                        type="text"
                        id="postal-code"
                        class="sub-label-input"
                        :placeholder="$t('form.postalCode')"
                        v-model="postalCode"
                        @input="setFieldPostalCode(postalCode)"
                    >
                </div>
                <div class="sub-label-item">
                    <label for="state" class="sub-label">{{ $t("form.stateProvince") }}</label>
                    <input
                        type="text"
                        id="state"
                        class="sub-label-input"
                        :placeholder="$t('form.stateProvince')"
                        v-model="state"
                        @input="setFieldState(state)"
                    >
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions } from "vuex";

export default {
    name: 'AddressSettings',
    props: {
        item: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            fieldAddress: this.item.address.fieldAddress,
            align: this.item.address.align,
            addressLine1: this.item.address.addressLine1,
            addressLine2: this.item.address.addressLine2,
            district: this.item.address.district,
            postalCode: this.item.address.postalCode,
            state: this.item.address.state,
            isRequired: this.item.address.isRequired,
            defaultAlign: false,
        };
    },
    computed: {
        requiredText() {
            return this.isRequired ? this.$t("chatWindow.open") : this.$t("chatWindow.closed");
        },
    },
    methods: {
        ...mapActions("forms", ["updateFormField"]),
        setLabelAlignAddress(align) {
            this.align = align;
            this.updateFields();
            this.$emit("update-label-align-address", align);
        },
        toggleRequired() {
            this.isRequired = !this.isRequired;
            this.updateFields();
            this.$emit("update-required", this.isRequired);
        },
        setFieldLabelAddress(label) {
            this.fieldAddress = label;
            this.$emit("update-field-label-address", label);
            this.updateFields();
        },
        setFieldAddressLine1(addressLine1) {
            this.addressLine1 = addressLine1;
            this.$emit("update-field-address-line-1", addressLine1);
            this.updateFields();
        },
        setFieldAddressLine2(addressLine2) {
            this.addressLine2 = addressLine2;
            this.$emit("update-field-address-line-2", addressLine2);
            this.updateFields();
        },
        setFieldDistrict(district) {
            this.district = district;
            this.$emit("update-field-district", district);
            this.updateFields();
        },
        setFieldPostalCode(postalCode) {
            this.postalCode = postalCode;
            this.$emit("update-field-postal-code", postalCode);
            this.updateFields();
        },
        setFieldState(state) {
            this.state = state;
            this.$emit("update-field-state", state);
            this.updateFields();
        },
        updateFields() {
            const updateFields = {
                address: {
                    fieldAddress: this.fieldAddress,
                    align: this.align,
                    addressLine1: this.addressLine1,
                    addressLine2: this.addressLine2,
                    district: this.district,
                    postalCode: this.postalCode,
                    state: this.state,
                    isRequired: this.isRequired,
                },
            };
            this.updateFormField({
                siteId: this.item.siteId,
                formFieldId: this.item._id,
                updateFields,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
#address-settings {
    padding: 15px;

    .form-field {
        display: flex;
        flex-direction: column;
        margin-bottom: 20px;

        .form-label {
            margin-bottom: 5px;
            font-weight: bold;
            color: #333;
        }

        .form-input {
            padding: 10px;
            border: 1px solid #ccc;
            border-radius: 4px;
            background-color: #f9f9f9;
            color: #333;
            font-size: 16px;
        }

        .button-group {
            display: flex;
            margin-bottom: 10px;

            .button {
                flex: 1;
                padding: 10px;
                margin-right: 5px;
                border: none;
                border-radius: 4px;
                background-color: #e0e0e0;
                color: #333;
                font-size: 14px;
                cursor: pointer;
                text-align: center;

                &.active {
                    background-color: #4a90e2;
                    color: #fff;
                }

                &:last-child {
                    margin-right: 0;
                }
            }
        }

        .form-description {
            margin-top: 5px;
            font-size: 12px;
            color: #666;
        }

        .toggle {
            display: flex;
            align-items: center;

            .toggle-option {
                padding: 10px;
                border: 1px solid #ccc;
                border-radius: 4px;
                background-color: #f0f0f0;
                color: #333;
                font-size: 14px;
                cursor: pointer;
                text-align: center;

                &.active {
                    background-color: #4a90e2;
                    color: #fff;
                }
            }
        }

        .sub-labels {
            grid-template-columns: 1fr 1fr;

            .sub-label-item {
                display: flex;
                flex-direction: column;

                .sub-label {
                    font-weight: bold;
                    color: #333;
                    margin-bottom: 5px;
                }

                .sub-label-input {
                    padding: 10px;
                    border: 1px solid #ccc;
                    border-radius: 4px;
                    background-color: #f9f9f9;
                    color: #333;
                    font-size: 16px;
                }
            }
        }

        .duplicate-button {
            padding: 10px;
            border: none;
            border-radius: 4px;
            background-color: #e0e0e0;
            color: #333;
            font-size: 14px;
            cursor: pointer;
            width: fit-content;

            &:hover {
                background-color: #d0d0d0;
            }
        }
    }
}
</style>
